import { Box, Grid, ListItemText, MenuItem } from "@mui/material";
import { ModalWrap } from "components/events/components/modalWrap";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import AnalysisTimeManager, {
  AnalysisTimeManagerData,
} from "components/analysisTimeManager/analysisTimeManager";
import analysisTimeManagerActions from "store/machineDetail/analysisTimeManager/actions";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAsyncReducer } from "store/selectTemplate";
import { getMachineLabelSubcategories } from "api/handlers/machine/getMachineLabelSubcategories";
import { labelField } from "shared/fieldParams";
import SearchMenu from "components/searchMenu";
import { getPlacements } from "api/handlers/placement/getPlacements";
import { IPlacementList } from "types/placement";
import Divider from "components/typography/heading/divider";
import Button from "components/button";
import { BlackCheckbox } from "components/checkbox";
import Spinner from "components/spinner";
import { createLabel, updateLabel } from "store/machineDetail/actions";
import moment from "moment";
import { urlFriendlyTZ } from "shared/helpers";
import { Local } from "components/dataLabeling/constants";
import { v4 as uuidv4 } from "uuid";

const { setData: setAnalysisTimeManagerData } = analysisTimeManagerActions;

type TProps = {
  open: boolean;
  data: AnalysisTimeManagerData | null | undefined;
  setData: (
    value: React.SetStateAction<AnalysisTimeManagerData | null | undefined>
  ) => void;
  machineId?: string | number;
  onSuccess?: () => void;
  title?: string;
  labelData?: any;
  isEdit?: boolean;
  onCancel: (e?: React.MouseEvent<HTMLButtonElement>) => void;
};

const AddLabelsModal = ({
  open,
  data,
  setData,
  machineId,
  onSuccess,
  title,
  labelData,
  isEdit,
  onCancel,
}: TProps) => {
  const dispatch: any = useDispatch();
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState("");
  const [labelSubcategoryId, setLabelSubcategoryId] = useState<any>(null);
  const [selectedValues, setSelectedValues] = useState<any>([]);
  const [hasError, setHasError] = useState(false);

  const {
    data: labelSubcategories,
    loading: loadingLabels,
    loadMore,
    loadingMore,
  } = useAsyncReducer(
    "labelSubcategoryDataSelect",
    getMachineLabelSubcategories,
    {
      pageSize: 100,
      q: searchValue,
      field: labelField,
      machine: machineId!,
    }
  );
  const { data: placementData, loading: loadingPlacement } = useAsyncReducer(
    "placementsSelect",
    getPlacements,
    {
      pageSize: 100,
      machineIds: [machineId],
    }
  );

  const handleSearch = useCallback((val: string) => {
    setSearchValue(val);
  }, []);

  const options = useMemo(() => {
    let options: { text: string; value: number }[] = [];
    if (labelSubcategories) {
      options = labelSubcategories.results.map((item: any) => ({
        value: item.id,
        text: item.name,
      }));
    }
    return options;
  }, [labelSubcategories]);

  const placementOptions = useMemo(() => {
    return placementData && placementData?.results
      ? placementData?.results?.map((placement: IPlacementList) => ({
          value: placement.id,
          text: placement.customName ?? placement.name,
        }))
      : [];
  }, [placementData]);

  const initialValues = useMemo(() => {
    return placementOptions.map((item: any) => item.value);
  }, [placementOptions]);

  useEffect(() => {
    if (!isEdit) {
      setSelectedValues(initialValues);
    }
  }, [initialValues]);
  useEffect(() => {
    if (isEdit && labelData) {
      setSelectedValues(labelData.placements || []);
      setLabelSubcategoryId(labelData.subcategory);

      const intermediate =
        labelData.intermediate_values &&
        labelData.intermediate_values.map((item: any) => ({
          date: item.datetime,
          value: item.value,
          uuid: uuidv4(),
        }));

      const newDates = labelData && [
        {
          date: labelData.start_datetime,
          value: labelData.start_value,
          uuid: uuidv4(),
        },
        ...intermediate,
        {
          date: labelData.end_datetime,
          value: labelData.end_value,
          uuid: uuidv4(),
        },
      ];
      setData({ dates: newDates });
    }
  }, [isEdit, labelData]);

  const handleChange = (value: number) => {
    if (selectedValues.includes(value)) {
      const newValues = selectedValues.filter(
        (selectedItem: number) => selectedItem !== value
      );
      setSelectedValues(newValues);
    } else {
      setSelectedValues([...selectedValues, value]);
    }
  };

  const handleSubmit = () => {
    const newDates =
      data?.dates.map((item) => ({
        ...item,
        date: moment(item.date).unix(),
      })) || [];

    function getMiddleItems(arr: any) {
      if (!arr || arr.length <= 2) {
        return [];
      }
      return arr.slice(1, -1);
    }

    const startItem = newDates[0];
    const endItem = newDates[newDates.length - 1];
    const middleItems = getMiddleItems(newDates);

    const labelSubcat = labelSubcategories.results.find(
      (sub: any) => labelSubcategoryId === sub.id
    );

    const newLabel = {
      item: { name: labelSubcat.name, subcategory: labelSubcat.id },
      start: startItem.date,
      end: endItem.date,
      placements: selectedValues,
      timezoneOffset: urlFriendlyTZ(false, Local) as any,
      eventId: null,
      machineId: machineId as number,
      startValue: startItem.value,
      endValue: endItem.value,
      intermediateValues: middleItems.length
        ? middleItems.map((item: any) => ({
            datetime: item.date,
            value: item.value,
          }))
        : null,
      onSuccess,
    };

    if (isEdit) {
      dispatch(updateLabel(labelData.id!, newLabel));
    } else {
      dispatch(createLabel(newLabel));
    }
  };

  const isDisabled: any = data?.dates?.some(
    (item: any) => item.value === null || item.value === ""
  );

  return (
    <ModalWrap
      open={open}
      onCancel={onCancel}
      title={title || t("analysisTimeManager.manageRULLabelDatePoints")}
      cta={
        isEdit
          ? t("confirmationBar.saveChanges")
          : t("confirmationBar.createRULLabel")
      }
      submitDisabled={
        ((data?.dates && data?.dates.length < 2) as boolean) ||
        !labelSubcategoryId ||
        hasError ||
        isDisabled
      }
      onSubmit={() => {
        handleSubmit();
        onCancel();
      }}
    >
      <Box flex={1} p={3}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <SearchMenu
              searchPlaceholder={t("search.searchLabelSubcategories")}
              value={labelSubcategoryId}
              disabled={false}
              loading={!labelSubcategories || loadingLabels}
              label={t("machine.detail.oneClickTraining.labelSubcategory")}
              id="testDashboardLabelSubcategoriesSelect"
              options={options}
              onChange={({ target }) => setLabelSubcategoryId(target.value)}
              loadMore={loadMore}
              loadingMore={loadingMore}
              onSearch={handleSearch}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Divider line>
              {t("machineDetail.editEvent.placementsTitle")}
            </Divider>
            {loadingPlacement ? (
              <Spinner />
            ) : (
              <>
                {placementOptions.length ? (
                  <>
                    <Button
                      disabled={
                        selectedValues.length >= placementOptions.length
                      }
                      onClick={() =>
                        setSelectedValues(
                          placementOptions.map((item: any) => item.value)
                        )
                      }
                    >
                      {t("filter.selectAll")}
                    </Button>
                    <Button
                      disabled={selectedValues.length === 0}
                      onClick={() => setSelectedValues([])}
                    >
                      {t("filter.deselectAll")}
                    </Button>{" "}
                  </>
                ) : (
                  <></>
                )}
                {placementOptions.map(
                  (item: { text: string; value: number }) => {
                    const checkedPlacement = selectedValues?.includes(
                      item.value
                    );

                    return (
                      <Box key={item.value}>
                        <MenuItem
                          data-value={item.value}
                          onClick={() => handleChange(item.value)}
                        >
                          <BlackCheckbox checked={checkedPlacement} />
                          <ListItemText primary={<>{item.text}</>} />
                        </MenuItem>
                      </Box>
                    );
                  }
                )}
              </>
            )}
          </Grid>
          <Grid item xs={12} md={12}>
            {data && (
              <AnalysisTimeManager
                data={data}
                setData={setData}
                isDatePoints={true}
                hasValue={true}
                isMonotonic={true}
                setHasMonotonicError={setHasError}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </ModalWrap>
  );
};
export default AddLabelsModal;
